/**
 * Short URL Route - /s/[id]
 *
 * Handles short URL access to shared analysis links with Open Graph tags.
 *
 * Features:
 * - Server-side rendering for social media crawlers (Open Graph meta tags)
 * - Validates short_id format (10 base62 chars: [0-9A-Za-z]{10})
 * - Fetches shared link data from validate_and_view_shared_link RPC
 * - Direct display of analysis (NO redirect to /shared)
 * - Graceful error handling for invalid/expired links
 *
 * URL Structure:
 * - /s/AbC123XyZ9 (10-character base62 short_id)
 *
 * Security:
 * - Short_id validated by RPC function
 * - RLS policies ensure only valid shared links are accessible
 * - View counting and expiration handled server-side
 *
 * SEO:
 * - Open Graph tags for rich social media previews
 * - Twitter Card tags for enhanced Twitter sharing
 * - Canonical URL points to /s/[id] format
 *
 * Phase 4 Implementation:
 * This route is part of the short URL migration (Task #5).
 * PRIMARY short URL format: /s/[id] (ONLY format - no redirect to /shared).
 */

import { useLocalSearchParams, useRouter, Stack } from "expo-router";
import { useTranslation } from "@dramadetector/shared";

// Hooks
import { useAuth } from "../../contexts/AuthContext";
import { useTier } from "../../hooks/useTier";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useSharedLinkData } from "../../hooks/shared-link";

// Components
import {
  SharedLinkHeader,
  SharedLinkError,
  SharedLinkLoading,
} from "../../components/shared-link";
import { ExplicitSharedResult } from "../../components/shared-link/ExplicitSharedResult";
import { LegacySharedLinkContent } from "../../components/shared-link/LegacySharedLinkContent";

// Feature flags
import { useL3AccordionsFlag } from "../../hooks/useFeatureFlags";

// Layout utilities
import { useResponsiveScreenPadding } from "../../components/layout/TabScreenWrapper";

// Analytics
import { captureEvent } from "../../lib/analytics";

// Types
import type { QuotaTier } from "../../components/trial/QuotaExhaustedModal";

/**
 * Short URL Screen - /s/[id]
 *
 * Flow:
 * 1. Extract short_id from URL params
 * 2. Validate short_id format (client-side)
 * 3. Call validate_and_view_shared_link RPC (server-side validation + view increment)
 * 4. If valid, fetch and display analysis directly (NO redirect)
 * 5. If invalid/expired, show error with helpful message
 */
export default function ShortUrlScreen() {
  const { t } = useTranslation("home");
  const router = useRouter();
  const params = useLocalSearchParams<{ id: string }>();
  const shortId = params.id;

  // Responsive screen padding
  const { bottomPadding } = useResponsiveScreenPadding();

  // Auth and session state (optional - user may be anonymous viewer)
  const { isAnonymous } = useAuth();

  // Tier level for tier-gated features (viewer's tier, not original creator's tier)
  const { tierLevel } = useTier();
  const { tier, isLoading: isEntitlementsLoading } = useEntitlements();

  // Feature flags
  const l3AccordionsEnabled = useL3AccordionsFlag();

  // Shared link data validation and fetching
  const {
    error,
    isValidating,
    validatedAnalysisId,
    analysisData,
    explicitResult,
  } = useSharedLinkData(shortId);

  // Derive current tier for viewer with loading guard.
  // Phase 2 (NocoDB #3869 close-out): use canonical `isAnonymous` from AuthContext.
  const currentTier: QuotaTier = (() => {
    if (isAnonymous) return "anonymous";
    if (isEntitlementsLoading || !tier) return "free";
    return tier as QuotaTier;
  })();

  // Map tier for components that don't support family_member
  const displayTier = currentTier === "family_member" ? "drama_plus" : currentTier;

  // Build Open Graph title from analysis data
  const ogTitle = analysisData?.title || "Drama Analysis - DramaDetector";

  /**
   * Navigate to home for new analysis
   */
  const handleNewAnalysis = () => {
    captureEvent("new_analysis_from_shared", {
      viewer_tier: currentTier,
      source: "short_url_page",
    });
    router.push("/(tabs)");
  };

  /**
   * Navigate to subscription
   */
  const handleUpgrade = () => {
    captureEvent("upgrade_clicked_from_shared", {
      current_tier: currentTier,
      source: "short_url_page",
    });
    router.push("/(tabs)/subscription");
  };

  // Handle validation errors
  if (error) {
    return (
      <>
        <SharedLinkHeader shortId={shortId || ""} analysisData={analysisData} />
        <Stack.Screen
          options={{
            title: ogTitle,
            headerShown: false,
          }}
        />
        <SharedLinkError error={error} onNewAnalysis={handleNewAnalysis} />
      </>
    );
  }

  // Handle loading state (validation or analysis fetch)
  if (isValidating) {
    return (
      <>
        <SharedLinkHeader shortId={shortId || ""} analysisData={analysisData} />
        <Stack.Screen
          options={{
            title: ogTitle,
            headerShown: false,
          }}
        />
        <SharedLinkLoading />
      </>
    );
  }

  // Explicit links intentionally never request or render the private full-analysis path.
  if (explicitResult) {
    return (
      <>
        <SharedLinkHeader shortId={shortId || ""} analysisData={analysisData} />
        <Stack.Screen options={{ title: ogTitle, headerShown: false }} />
        <ExplicitSharedResult shortId={shortId || ""} result={explicitResult} />
      </>
    );
  }

  return (
    <LegacySharedLinkContent
      shortId={shortId || ""}
      validatedAnalysisId={validatedAnalysisId}
      analysisData={analysisData}
      currentTier={currentTier}
      displayTier={displayTier}
      tierLevel={tierLevel}
      l3AccordionsEnabled={l3AccordionsEnabled}
      bottomPadding={bottomPadding}
      onNewAnalysis={handleNewAnalysis}
      onUpgrade={handleUpgrade}
      t={t}
    />
  );
}
